import { HttpErrorResponse } from '@angular/common/http';
import { OrderStatus } from '@seatix/shared-domain-order';

/**
 * Confirm answers 409 while the per-order payment lock is held by the webhook
 * or the cart expiry job: the order is still `cart` at that moment, so the
 * payment may settle seconds later. It is a state signal, not an error —
 * never retried, never surfaced as a confirm failure.
 */
export function isOrderProcessingConflict(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 409 &&
    (error.error as { message?: unknown } | null)?.message ===
      'Order is being processed'
  );
}

/**
 * A failure that says nothing about the order: the request never got an
 * answer (status 0) or the server broke while answering (5xx).
 */
export function isTransientHttpError(error: unknown): boolean {
  return (
    error instanceof HttpErrorResponse &&
    (error.status === 0 || error.status >= 500)
  );
}

export const ORDER_CONFIRMATION_POLL_INTERVAL_MS = 2_000;

/**
 * Sized off the API's own budget: the lock TTL is 30 s and the webhook job
 * retries twice more with ~15 s releases between attempts, so a webhook that
 * wins can settle up to ~60 s after confirm answered 409.
 */
export const ORDER_CONFIRMATION_POLL_WINDOW_MS = 90_000;

/**
 * A payment the webhook rejected leaves the order `cancelled` (seats released)
 * and a failed charge after the sale leaves it `failed`; neither can be paid
 * any more.
 */
export function isFailedOrderStatus(status: OrderStatus): boolean {
  return status === 'cancelled' || status === 'failed';
}

/**
 * How the wait for an unconfirmed order ended without a confirmed order.
 * `cancelled` is final (the order is `cancelled` or `failed`); `timeout` only
 * means the order had not settled yet — it can still reach `pending` later.
 */
export type OrderConfirmationOutcome = 'cancelled' | 'timeout';

/**
 * Ends the confirmation call status of a failed payment. The buyer never reads
 * it: the success page renders the failure screen for that outcome instead.
 */
export const ORDER_CONFIRMATION_CANCELLED_MESSAGE = 'El pago no se completó.';

export const ORDER_CONFIRMATION_TIMEOUT_MESSAGE =
  'Tu pago sigue procesándose. Si se aplicó el cargo, tus boletos llegarán a tu correo en unos minutos.';

export const ORDER_CONFIRMATION_OUTCOME_MESSAGES = {
  cancelled: ORDER_CONFIRMATION_CANCELLED_MESSAGE,
  timeout: ORDER_CONFIRMATION_TIMEOUT_MESSAGE,
} as const satisfies Record<OrderConfirmationOutcome, string>;
